<script setup lang="ts">
import { onMounted } from 'vue';
import HeroSection from '@/components/HeroSection.vue';
import FeatureCards from '@/components/FeatureCards.vue';
import Footer from '@/components/Footer.vue';
import StartButton from '@/components/button/StartButton.vue';
import { animate, stagger } from 'animejs';

onMounted(() => {

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const target = entry.target;
      const animateInElements = target.querySelectorAll('.animate-on-scroll');
      
      if (entry.isIntersecting) {
        target.classList.add('active');
        if (animateInElements.length > 0) {
          animate(animateInElements, {
            translateY: [30, 0],
            opacity: [0, 1],
            delay: stagger(100),
            duration: 800,
            easing: 'easeOutExpo'
          });
        }
      } else if (entry.boundingClientRect.top > 0) {
        target.classList.remove('active');
        animate(animateInElements, {
          translateY: 40,
          opacity: 0,
          duration: 0
        });
      }
    });
  }, { threshold: 0.05 });

  document.querySelectorAll('.scroll-reveal, .animate-on-scroll').forEach(el => {
    revealObserver.observe(el);
  });
});
</script>

<template>
  <div class="landing-page">
    <HeroSection />


    <FeatureCards />

    <section class="problem-section scroll-reveal">
      <div class="problem-content">
        <h2 class="animate-on-scroll">Today, it's <span class="text-accent"> just a screenshot.</span></h2>
        <p class="problem-desc animate-on-scroll">
          ... with no bridge <span class="text-accent">to your brand</span><br />
          No CTA<span class="text-accent">.</span>
          No Add Leg<span class="text-accent">.</span>
          No tweak<span class="text-accent">.</span>
          No bet<span class="text-accent">.</span><br />
          <span class="text-accent">The chat moves on.</span>

        </p>
        <div class="problem-visual glass animate-on-scroll">
          <div class="chat-mockup">
            <div class="chat-bubble left">Guys, place it?</div>
            <div class="chat-bubble right">Which APP tho?</div>
            <div class="screenshot-label">CAN'T TAP</div>
          </div>
        </div>
      </div>
    </section>
    
    <section class="cta-section scroll-reveal">
      <div class="cta-card glass">
        <h2 class="animate-on-scroll">Let's do it<br />
          <span class="text-gradient">Ready to launch.</span></h2>
        <div class="cta-bullets animate-on-scroll">
          <div class="bullet">We bring the product: BetPic, tailored to your brand.</div>
          <div class="bullet">Create a microsite — that's your entire lift.</div>
          <div class="bullet">We consume your prices. We do the work.</div>
          <div class="bullet">A truly functional layer, inside your customer's group chats.</div>
        </div>
        <div class="cta-btns animate-on-scroll">
          <StartButton />
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>


<style scoped>
.landing-page {
  width: 100%;
}

.cta-section {
  padding: 100px 5%;
  display: flex;
  justify-content: center;
}

.cta-card {
  width: 100%;
  max-width: 1000px;
  padding: 80px 40px;
  border-radius: 40px;
  text-align: center;
  background: radial-gradient(circle at top right, rgba(230, 0, 0, 0.1), transparent);
}

.cta-card h2 {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 24px;
}

.cta-card p {
  color: var(--text-secondary);
  font-size: 1.2rem;
  margin-bottom: 40px;
}


.cta-btns {
  display: flex;
  justify-content: center;
}


.problem-section {
  padding: 120px 5%;
  text-align: center;
}

.problem-content h2 {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 24px;
}

.problem-desc {
  font-size: 1.4rem;
  color: var(--text-secondary);
  margin-bottom: 60px;
  line-height: 1.6;
}


.problem-visual {
  max-width: 600px;
  margin: 0 auto;
  padding: 40px;
  border-radius: 30px;
  position: relative;
}

.chat-mockup {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.chat-bubble {
  padding: 12px 20px;
  border-radius: 18px;
  max-width: 80%;
  font-size: 1.1rem;
}

.chat-bubble.left {
  background: var(--charcoal);
  align-self: flex-start;
  border-bottom-left-radius: 4px;
}

.chat-bubble.right {
  background: var(--red-primary);
  align-self: flex-end;
  border-bottom-right-radius: 4px;
}

.screenshot-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-15deg);
  background: rgba(230, 0, 0, 0.9);
  color: white;
  padding: 10px 30px;
  font-weight: 800;
  font-size: 1.5rem;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.cta-bullets {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 40px;
  text-align: left;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.bullet {
  position: relative;
  padding-left: 30px;
  color: var(--text-secondary);
  font-size: 1.1rem;
}

.bullet::before {
  content: "—";
  position: absolute;
  left: 0;
  color: var(--red-primary);
  font-weight: 800;
}

@media (max-width: 768px) {
  .problem-section {
    padding: 80px 5%;
  }

  .problem-content h2 {
    font-size: 2.2rem;
  }

  .problem-desc {
    font-size: 1.1rem;
    margin-bottom: 40px;
  }

  .problem-visual {
    padding: 30px 20px;
    border-radius: 20px;
    min-height: 220px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .chat-mockup {
    gap: 12px;
  }

  .chat-bubble {
    font-size: 0.95rem;
    padding: 10px 16px;
    max-width: 90%;
  }

  .screenshot-label {
    font-size: 1.1rem;
    padding: 10px 24px;
    white-space: nowrap;
    transform: translate(-50%, -50%) rotate(-8deg);
  }

  .cta-section {
    padding: 60px 5%;
  }

  .cta-card {
    padding: 40px 20px;
    border-radius: 24px;
  }

  .cta-card h2 {
    font-size: 2.2rem;
  }

  .cta-card p {
    font-size: 1rem;
    margin-bottom: 30px;
  }

  .cta-bullets {
    margin-bottom: 30px;
  }

  .bullet {
    font-size: 0.95rem;
    padding-left: 24px;
  }
}

</style>
